import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Card,
  CardActionArea,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import {
  RouteRounded,
  VerifiedRounded,
  GroupsRounded,
  PublicRounded,
  ChevronRightRounded,
} from '@mui/icons-material';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import type { ProgramStatus } from '@dosfilos/domain';
import { db } from '../../lib/firebase';
import { useTenantStore } from '../../stores/tenant.store';
import { CAP_CONTENT_MAX_WIDTH } from './components/CapPageHeader';
import { errorText } from '../../lib/error-text';

// ─── Camino TMAI — hub (Parte B) ────────────────────────────────────────────
// Lateral entry point that indexes every program's CAP certification journey
// plus institutional readiness, and reserves the Fase 2 surfaces. A launcher:
// per-program compliance is computed inside each Camino, not recomputed here.

interface CapTarget {
  capCode: 'BSC' | 'DEP';
  level: 'undergraduate' | 'graduate';
}
interface ProgramRow {
  id: string;
  name: string;
  status: ProgramStatus;
  deleted: boolean;
  capTarget?: CapTarget;
}

export function CapHubPage(): JSX.Element {
  const { t } = useTranslation('cap');
  const navigate = useNavigate();
  const { currentTenantId } = useTenantStore();

  const [programs, setPrograms] = useState<ProgramRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!currentTenantId) return;
    let alive = true;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const snap = await getDocs(
          query(collection(db, `tenants/${currentTenantId}/programs`), orderBy('name')),
        );
        if (!alive) return;
        setPrograms(
          snap.docs
            .map((d) => {
              const data = d.data() as {
                name?: string;
                capTarget?: CapTarget;
                status?: ProgramStatus;
                deletedAt?: unknown;
              };
              return {
                id: d.id,
                name: data.name ?? d.id,
                status: data.status ?? 'active',
                deleted: !!data.deletedAt,
                ...(data.capTarget ? { capTarget: data.capTarget } : {}),
              };
            })
            // Un programa dado de baja NO se certifica. Ofrecerlo acá mandó al
            // coordinador a trabajar sobre el programa equivocado más de una vez:
            // en demo convivían «Diplomado de Predicación Expositiva» (vigente) y
            // «Diploma en Predicación Expositiva (Demo)» (desactivado), y el hub
            // los mostraba iguales.
            .filter((p) => !p.deleted && p.status === 'active'),
        );
      } catch (e) {
        if (alive) setError(errorText(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [currentTenantId]);

  const targeted = programs.filter((p) => p.capTarget);
  const untargeted = programs.filter((p) => !p.capTarget);

  return (
    <Box>
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.default',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <RouteRounded color="primary" />
          <Box>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
              {t('hub.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('hub.subtitle')}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Box sx={{ px: 3, py: 3, pb: 6, maxWidth: CAP_CONTENT_MAX_WIDTH, mx: 'auto' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={4}>
            {/* Programs certifying with TMAI */}
            <Box>
              <Typography variant="subtitle1" fontWeight={700} mb={0.5}>
                {t('hub.programs.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                {t('hub.programs.help')}
              </Typography>
              {targeted.length === 0 ? (
                <Alert severity="info">{t('hub.programs.none')}</Alert>
              ) : (
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  gap={2}
                  sx={{ '& > *': { flex: '1 1 320px', maxWidth: 480 } }}
                >
                  {targeted.map((p) => (
                    <Card key={p.id} variant="outlined" sx={{ borderRadius: 3 }}>
                      <CardActionArea
                        onClick={() => navigate(`/cap/${p.id}/camino`)}
                        sx={{ p: 2.5, height: '100%' }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          spacing={1}
                        >
                          <Stack direction="row" alignItems="center" spacing={1.25} minWidth={0}>
                            <VerifiedRounded color="primary" fontSize="small" />
                            <Typography fontWeight={700} noWrap>
                              {p.name}
                            </Typography>
                          </Stack>
                          <ChevronRightRounded color="action" />
                        </Stack>
                        <Stack direction="row" spacing={0.75} mt={1.5} flexWrap="wrap">
                          <Chip
                            size="small"
                            color="primary"
                            variant="outlined"
                            label={t(`plos.caps.${p.capTarget!.capCode}`)}
                          />
                          <Chip
                            size="small"
                            variant="outlined"
                            label={t(`levels.${p.capTarget!.level}`)}
                          />
                        </Stack>
                      </CardActionArea>
                    </Card>
                  ))}
                </Stack>
              )}
            </Box>

            {/* Programs without a CAP target yet */}
            {untargeted.length > 0 && (
              <Box>
                <Typography variant="subtitle1" fontWeight={700} mb={0.5}>
                  {t('hub.untargeted.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  {t('hub.untargeted.help')}
                </Typography>
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  gap={2}
                  sx={{ '& > *': { flex: '1 1 320px', maxWidth: 480 } }}
                >
                  {untargeted.map((p) => (
                    <Card
                      key={p.id}
                      variant="outlined"
                      sx={{ borderRadius: 3, bgcolor: 'action.hover' }}
                    >
                      <CardActionArea
                        onClick={() => navigate(`/cap/${p.id}/camino`)}
                        sx={{ p: 2.5 }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          spacing={1}
                        >
                          <Typography fontWeight={600} color="text.secondary" noWrap>
                            {p.name}
                          </Typography>
                          <ChevronRightRounded color="action" />
                        </Stack>
                      </CardActionArea>
                    </Card>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Institutional + Fase 2 surfaces (reserved) */}
            <Box>
              <Typography variant="subtitle1" fontWeight={700} mb={2}>
                {t('hub.institutional.title')}
              </Typography>
              <Stack
                direction="row"
                flexWrap="wrap"
                gap={2}
                sx={{ '& > *': { flex: '1 1 280px', maxWidth: 480 } }}
              >
                {(
                  [
                    { icon: <VerifiedRounded />, key: 'requirements', active: true },
                    { icon: <GroupsRounded />, key: 'reviewer', active: false },
                    { icon: <PublicRounded />, key: 'network', active: false },
                  ] as const
                ).map((c) => {
                  const inner = (
                    <>
                      <Stack direction="row" alignItems="center" spacing={1.25} mb={1}>
                        {c.icon}
                        <Typography fontWeight={700}>{t(`hub.${c.key}.title`)}</Typography>
                        {!c.active && (
                          <Chip size="small" label={t('hub.networkPhase')} sx={{ ml: 'auto' }} />
                        )}
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {t(`hub.${c.key}.body`)}
                      </Typography>
                    </>
                  );
                  return c.active ? (
                    <Card key={c.key} variant="outlined" sx={{ borderRadius: 3 }}>
                      <CardActionArea
                        sx={{ p: 2.5, height: '100%' }}
                        onClick={() => navigate('/cap/institucional')}
                      >
                        {inner}
                      </CardActionArea>
                    </Card>
                  ) : (
                    <Card
                      key={c.key}
                      variant="outlined"
                      sx={{ borderRadius: 3, p: 2.5, opacity: 0.6 }}
                    >
                      {inner}
                    </Card>
                  );
                })}
              </Stack>
            </Box>
          </Stack>
        )}
      </Box>
    </Box>
  );
}
